<script setup>
import HandComponent from '@/components/HandComponent.vue'
import RoomComponent from '@/components/RoomComponent.vue'
import CardPreferencesModal from '@/components/CardPreferencesModal.vue'
import UserProfileModal from '@/components/UserProfileModal.vue'
import { onBeforeUnmount, onMounted, ref, watch, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import { store } from '@/store'
import { useAuth0 } from '@auth0/auth0-vue'
import Fireworks from '@fireworks-js/vue'
import LoginSignUpButton from '@/components/common/LoginSignUpButton.vue'
import { useRoomStore } from '@/stores/room'
import AdjustmentsHorizontalIcon from '@/assets/icons/outline/AdjustmentsHorizontalIcon.vue'
import IconButtonComponent from '@/components/common/IconButtonComponent.vue'
import EyeSlashIcon from '@/assets/icons/outline/EyeSlashIcon.vue'
import EyeIcon from '@/assets/icons/outline/EyeIcon.vue'
import BugAntIcon from '@/assets/icons/outline/BugAntIcon.vue'
import DarkModeToggle from '@/components/DarkModeToggle.vue'
import ButtonComponent from '@/components/common/ButtonComponent.vue'

const route = useRoute()
const roomStore = useRoomStore()

const cardPreferencesModalOpen = ref(false)
const { isAuthenticated } = useAuth0()
const fireworks = ref(null)


onBeforeMount(() => {
  roomStore.resetLoadingState()
})

onMounted(async () => {
  const requestedRoomId = route.params.id
  roomStore.joinRoom(requestedRoomId)
  store.setRoomId(requestedRoomId)
})

onBeforeUnmount(async () => {
  roomStore.cleanup()
})

watch(
  () => roomStore.isRevealed,
  (newValue) => {
    if (newValue) {
      checkForFireworks()
    }
  }
)

function checkForFireworks() {
  const scores = Object.values(roomStore.round?.votes || {}).map((entry) => entry.score)
  const areAllVotesIdentical = scores.every((score) => score === scores[0])

  if (areAllVotesIdentical) {
    fireworks.value?.start()

    setTimeout(function () {
      fireworks.value?.waitStop()
    }, 5000)
  }
}
</script>

<template v-if="roomId">
  <header class="fixed w-full bg-surface-secondary bg-opacity-80 backdrop-blur-md shadow-sm z-10">
    <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
      <router-link to="/" class="text-accent block text-3xl font-bold tracking-wide">
        æsti
      </router-link>
      <div class="flex items-center space-x-6" v-if="!roomStore.isLoading">
        <IconButtonComponent id="report-a-bug">
          <BugAntIcon />
        </IconButtonComponent>
        <DarkModeToggle />
        <IconButtonComponent @click="roomStore.toggleSpectate" :active="roomStore.isSpectating">
          <EyeSlashIcon v-if="roomStore.isSpectating" />
          <EyeIcon v-else />
        </IconButtonComponent>
        <IconButtonComponent @click="cardPreferencesModalOpen = true">
          <AdjustmentsHorizontalIcon />
        </IconButtonComponent>
        <UserProfileModal/>
      </div>
    </nav>
  </header>
  <div class="overflow-hidden h-full">
    <div class="relative h-full w-full">
      <Fireworks class="absolute h-full w-full pointer-events-none" ref="fireworks" :autostart="false" />
      <div class="flex h-full w-full flex-row">
        <div class="relative flex h-full w-full flex-col items-center justify-center">
          <div v-if="!isAuthenticated || roomStore.hasUserError"
            class="absolute z-10 flex h-full w-full flex-col items-center justify-center gap-8 backdrop-blur-lg">
            <div class="rounded-lg text-neutral">You're currently not logged in.</div>
            <LoginSignUpButton />
          </div>
          <div v-else-if="roomStore.hasRoomError"
            class="absolute z-10 flex h-full w-full items-center justify-center backdrop-blur-lg">
            <div class="text-center">
              <h1 class="mt-4 text-3xl font-bold tracking-tight text-neutral sm:text-5xl">
                Room not found
              </h1>
              <p class="mt-6 text-base leading-7 text-primary">
                Sorry, we couldn’t find the room you’re looking for.
              </p>
              <div class="mt-10 flex items-center justify-center gap-x-6">
                <router-link to="/">
                  <ButtonComponent primary>
                    Home
                  </ButtonComponent>
                </router-link>
              </div>
            </div>
          </div>
          <div v-else-if="roomStore.isLoading" class="text-neutral">
            <div class="loader"></div>
          </div>
          <div class="flex h-full w-full flex-col items-center justify-center" v-else>
            <CardPreferencesModal v-if="cardPreferencesModalOpen" @close="cardPreferencesModalOpen = false" />
            <RoomComponent />
            <HandComponent />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: rgba(233, 233, 233, 0.8);
}

::-webkit-scrollbar-thumb {
  background: #bdbdbd;
}

::-webkit-scrollbar-thumb:hover {
  background: #ff0000;
}

/* HTML: <div class="loader"></div> */
.loader {
  margin-top: 8px;
  width: 20px;
  aspect-ratio: 1;
  position: relative;
}

.loader:before,
.loader:after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  margin: -8px 0 0 -8px;
  width: 16px;
  aspect-ratio: 1;
  background: rgb(var(--primary));
  animation:
    l1-1 2s infinite,
    l1-2 .5s infinite;
}

.loader:after {
  background: rgb(var(--accent));
  animation-delay: -1s, 0s;
}

@keyframes l1-1 {
  0% {
    top: 0;
    left: 0
  }

  25% {
    top: 100%;
    left: 0
  }

  50% {
    top: 100%;
    left: 100%
  }

  75% {
    top: 0;
    left: 100%
  }

  100% {
    top: 0;
    left: 0
  }
}

@keyframes l1-2 {

  80%,
  100% {
    transform: rotate(0.5turn)
  }
}
</style>
